import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { useAdmin } from "../../contexts/AdminContext";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { apiCreatePaymentIntent, apiCreateOrder } from "../../lib/api";

type PayMethod = "card" | "paypal" | "transfer";

function StripeCardForm() {
  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1">Kreditkartendaten</label>
        <div className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm focus-within:border-secondary transition-colors">
          <CardElement 
            options={{
              style: {
                base: { fontSize: "14px", color: "#334155", "::placeholder": { color: "#94a3b8" } },
                invalid: { color: "#ef4444" }
              }
            }} 
          />
        </div>
      </div>
    </div>
  );
}

function PaiementInner() {
  const stripe = useStripe();
  const elements = useElements();
  const [searchParams] = useSearchParams();
  const rawParam = searchParams.get("package") || "0";
  const { settings } = useAdmin();

  // Support both ?package=2 (numeric index) and ?package=pack-2 (legacy format)
  let pkgIndex = 0;
  if (rawParam.startsWith("pack-")) {
    pkgIndex = parseInt(rawParam.replace("pack-", "")) || 0;
  } else {
    pkgIndex = parseInt(rawParam) || 0;
  }
  const pkgId = `pack-${pkgIndex}`;
  const selectedPkg = settings.packages[pkgIndex] || settings.packages[0];

  const pkg = {
    name: selectedPkg?.name || "Forfait",
    tickets: selectedPkg?.count || 1,
    bonus: 0,
    price: parseFloat(selectedPkg?.price.replace("€", "").trim()) || 0,
    color: selectedPkg?.popular ? "text-primary" : "text-sky-600",
  };

  const [payMethod, setPayMethod] = useState<PayMethod>("card");
  const [accepted, setAccepted] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const [userInfo, setUserInfo] = useState({ firstname: "", lastname: "", email: "", phone: "" });
  
  // Set initial payment method based on what's enabled
  useEffect(() => {
    if (!settings.paymentProviders.stripeEnabled && payMethod === "card") {
      if (settings.paymentProviders.paypalEnabled) setPayMethod("paypal");
      else if (settings.paymentProviders.bankTransferEnabled) setPayMethod("transfer");
    }
  }, [settings.paymentProviders]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accepted) return;
    
    setLoading(true);

    try {
      if (payMethod === "card") {
        if (!stripe || !elements) return;

        // 1. Demander le clientSecret au backend
        const { clientSecret, error: serverError } = await apiCreatePaymentIntent({ amount: pkg.price, packageId: pkgId, userInfo });
        
        if (serverError) {
          alert(`Serverfehler: ${serverError}`);
          setLoading(false);
          return;
        }

        // 2. Confirmer le paiement avec Stripe Elements
        const cardElement = elements.getElement(CardElement);
        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement!,
            billing_details: {
              name: `${userInfo.firstname} ${userInfo.lastname}`,
              email: userInfo.email,
            },
          },
        });

        if (error) {
          alert(error.message);
          setLoading(false);
          return;
        }

        if (paymentIntent && paymentIntent.status === "succeeded") {
          // 3. Sauvegarder la commande en BDD
          await apiCreateOrder({ userInfo, packageId: pkgId, amount: pkg.price, paymentMethod: "card", paymentStatus: "paid", paymentId: paymentIntent.id, ticketCount: pkg.tickets + pkg.bonus });
          setSuccess(true);
        }
      } else if (payMethod === "transfer") {
        // Enregistrer la commande en attente de virement
        await apiCreateOrder({ userInfo, packageId: pkgId, amount: pkg.price, paymentMethod: "transfer", paymentStatus: "pending", ticketCount: pkg.tickets + pkg.bonus });
        setSuccess(true);
      }
      // Note: PayPal is handled by its own component, so it doesn't trigger handleSubmit
    } catch (err) {
      console.error(err);
      alert("Ein Fehler ist aufgetreten.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <main className="min-h-screen font-inter bg-slate-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center min-h-[80vh] px-6 text-center">
          <div className="bg-white rounded-2xl p-12 max-w-md w-full shadow-sm border border-slate-100">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-checkbox-circle-line text-4xl text-secondary"></i>
            </div>
            <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-3">Zahlung bestätigt!</h2>
            <p className="text-slate-500 mb-2">
              Herzlichen Glückwunsch! Sie nehmen an der Ziehung #52 mit dem Paket <strong className={pkg.color}>{pkg.name}</strong> teil.
            </p>
            <p className="text-slate-500 text-sm mb-6">
              Ihre <strong>{pkg.tickets + pkg.bonus} Teilnahmenummern</strong> wurden Ihnen innerhalb von 2 Minuten per E-Mail zugesandt.
            </p>
            <div className="bg-slate-50 rounded-xl p-4 mb-8 text-left space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Paket</span>
                <span className="font-semibold text-slate-900">{pkg.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Lose</span>
                <span className="font-semibold text-slate-900">{pkg.tickets} + {pkg.bonus} Bonus</span>
              </div>
              <div className="flex justify-between text-sm border-t border-slate-200 pt-2 mt-2">
                <span className="text-slate-700 font-medium">Gesamt bezahlt</span>
                <span className="font-bold text-secondary-dark text-base">{pkg.price} €</span>
              </div>
            </div>
            <Link
              to="/"
              className="w-full block text-center bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              Zurück zur Startseite
            </Link>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen font-inter bg-slate-50">
      <Navbar />

      <div className="max-w-6xl mx-auto px-6 pt-32 pb-20">
        {/* Breadcrumb */}
        <nav className="text-sm text-slate-400 mb-8" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-slate-600 cursor-pointer">Startseite</Link>
          <span className="mx-2">/</span>
          <Link to="/forfaits" className="hover:text-slate-600 cursor-pointer">Pakete</Link>
          <span className="mx-2">/</span>
          <span className="text-slate-700 font-medium">Bezahlung</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
          {/* Left: Form (60%) */}
          <form
            onSubmit={handleSubmit}
            className="lg:col-span-3 space-y-8"
          >
            <h1 className="font-playfair text-3xl font-bold text-slate-900">
              Bestellung abschließen
            </h1>

            {/* Personal Info */}
            <div className="bg-white rounded-2xl p-7 border border-slate-100">
              <h2 className="font-semibold text-slate-900 mb-5 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-sky-100 text-sky-600 rounded-full text-sm font-bold">1</span>
                Persönliche Informationen
              </h2>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Vorname *</label>
                  <input type="text" value={userInfo.firstname} onChange={(e) => setUserInfo({...userInfo, firstname: e.target.value})} name="firstname" required placeholder="Marie" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-secondary transition-colors" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Nachname *</label>
                  <input type="text" value={userInfo.lastname} onChange={(e) => setUserInfo({...userInfo, lastname: e.target.value})} name="lastname" required placeholder="Mustermann" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-secondary transition-colors" />
                </div>
              </div>
              <div className="mb-4">
                <label className="text-xs font-medium text-slate-600 block mb-1">E-Mail *</label>
                <input type="email" value={userInfo.email} onChange={(e) => setUserInfo({...userInfo, email: e.target.value})} name="email" required placeholder="marie@beispiel.de" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-secondary transition-colors" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Telefon</label>
                <input type="tel" value={userInfo.phone} onChange={(e) => setUserInfo({...userInfo, phone: e.target.value})} name="phone" placeholder="+49 40 12 34 56 78" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-secondary transition-colors" />
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-white rounded-2xl p-7 border border-slate-100">
              <h2 className="font-semibold text-slate-900 mb-5 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-sky-100 text-sky-600 rounded-full text-sm font-bold">2</span>
                Zahlungsmethode
              </h2>

              {/* Tabs */}
              <div className="flex gap-3 mb-6">
                {[
                  { id: "card", enabled: settings.paymentProviders.stripeEnabled, icon: "ri-bank-card-line", label: "Karte" },
                  { id: "paypal", enabled: settings.paymentProviders.paypalEnabled, icon: "ri-paypal-line", label: "PayPal" },
                  { id: "transfer", enabled: settings.paymentProviders.bankTransferEnabled, icon: "ri-building-line", label: "Überweisung" },
                ].filter(m => m.enabled).map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setPayMethod(m.id as PayMethod)}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap border-2 ${payMethod === m.id ? "border-sky-500 bg-sky-50 text-sky-600" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}
                  >
                    <span className="w-4 h-4 inline-flex items-center justify-center mr-1"><i className={`${m.icon} text-sm`}></i></span>
                    {m.label}
                  </button>
                ))}
              </div>

              {payMethod === "card" && <StripeCardForm />}
              {payMethod === "paypal" && (
                <div className="py-4 relative z-0">
                  {settings.paymentProviders?.paypalClientId ? (
                    <PayPalScriptProvider options={{ clientId: settings.paymentProviders.paypalClientId, currency: "EUR" }}>
                      <PayPalButtons 
                        disabled={!accepted || !userInfo.firstname || !userInfo.lastname || !userInfo.email}
                        createOrder={(data, actions) => {
                          return actions.order.create({
                            intent: "CAPTURE",
                            purchase_units: [{
                              description: pkg.name,
                              amount: { value: pkg.price.toString(), currency_code: "EUR" }
                            }]
                          });
                        }}
                        onApprove={async (data, actions) => {
                          if (!actions.order) return;
                          const order = await actions.order.capture();
                          if (order && order.status === "COMPLETED") {
                            await apiCreateOrder({ userInfo, packageId: pkgId, amount: pkg.price, paymentMethod: "paypal", paymentStatus: "paid", paymentId: order.id, ticketCount: pkg.tickets + pkg.bonus });
                            setSuccess(true);
                          }
                        }}
                      />
                    </PayPalScriptProvider>
                  ) : (
                    <div className="text-center p-6 bg-amber-50 rounded-xl text-amber-700 text-sm">
                      Die PayPal-API wurde vom Administrator noch nicht konfiguriert.
                    </div>
                  )}
                </div>
              )}
              {payMethod === "transfer" && (
                <div className="bg-slate-50 rounded-xl p-5 space-y-2 text-sm">
                  <p className="font-semibold text-slate-800">Bankverbindung :</p>
                  <p className="text-slate-600">Inhaber : <strong>{settings.paymentProviders?.bankHolder || "In Erwartung"}</strong></p>
                  <p className="text-slate-600">IBAN : {settings.paymentProviders?.bankIban || "In Erwartung"}</p>
                  <p className="text-slate-600">BIC : {settings.paymentProviders?.bankBic || "In Erwartung"}</p>
                  <p className="text-slate-600">Verwendungszweck : <strong>THV-{pkgId.toUpperCase()}</strong></p>
                  <p className="text-amber-600 text-xs mt-2">Ihre Teilnahme wird aktiviert, sobald die Überweisung eingegangen ist (1-2 Werktage).</p>
                </div>
              )}
            </div>

            {/* Accept Terms */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="accept"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-1 w-4 h-4 secondary-light-secondary cursor-pointer"
              />
              <label htmlFor="accept" className="text-sm text-slate-600 cursor-pointer leading-relaxed">
                Ich akzeptiere die{" "}
                <Link to="/mentions-legales" className="text-sky-500 hover:underline">Allgemeinen Geschäftsbedingungen</Link>
                {" "}und die{" "}
                <Link to="/mentions-legales" className="text-sky-500 hover:underline">Spielregeln</Link>.
                Ich bestätige, die Datenschutzbestimmungen gelesen zu haben.
              </label>
            </div>

            {/* Submit */}
            {payMethod !== "paypal" && (
              <button
                type="submit"
                disabled={!accepted || loading}
                className={`w-full font-bold py-4 rounded-xl text-base flex items-center justify-center gap-3 transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  accepted
                    ? "bg-gradient-to-r from-secondary to-sky-500 hover:from-secondary-dark hover:to-sky-600 text-white shadow-xl hover:-translate-y-0.5"
                    : "bg-slate-200 text-slate-400 cursor-not-allowed"
                }`}
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-lg"></i>
                    Wird verarbeitet...
                  </>
                ) : (
                  <>
                    <span className="w-5 h-5 flex items-center justify-center"><i className="ri-lock-line text-lg"></i></span>
                    Bestätigen und Bezahlen {pkg.price} €
                  </>
                )}
              </button>
            )}
          </form>

          {/* Right: Summary (40%) */}
          <div className="lg:col-span-2 lg:sticky lg:top-28">
            <div className="bg-white rounded-2xl border border-slate-100 p-6">
              <h2 className="font-semibold text-slate-900 mb-5">Zusammenfassung</h2>

              {/* Package Visual */}
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-5 mb-5">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                    <i className="ri-ticket-2-line text-2xl text-primary"></i>
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">{pkg.name}</p>
                    <p className="text-slate-500 text-xs">{pkg.tickets + pkg.bonus} Lose insgesamt</p>
                  </div>
                </div>

                {/* Line Items */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">{pkg.tickets} Lose × {(pkg.price / pkg.tickets).toFixed(2)}€</span>
                    <span className="text-slate-700 font-medium">{pkg.price}€</span>
                  </div>
                  {pkg.bonus > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">{pkg.bonus} Bonus-Lose</span>
                      <span className="text-secondary-dark font-medium">KOSTENLOS</span>
                    </div>
                  )}
                  <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between items-center">
                    <span className="font-semibold text-slate-800">Gesamt</span>
                    <span className="font-bold text-2xl text-slate-900">{pkg.price}€</span>
                  </div>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="space-y-3">
                {[
                  { icon: "ri-lock-line", label: "100% sichere Zahlung (SSL)" },
                  { icon: "ri-mail-check-line", label: "Lose erhalten in weniger als 2 Min." },
                  { icon: "ri-award-line", label: "Notariell beglaubigte Ziehung" },
                  { icon: "ri-customer-service-2-line", label: "Support 7 Tage die Woche bei Bedarf" },
                ].map((t, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm text-slate-600">
                    <span className="w-7 h-7 flex items-center justify-center bg-emerald-50 text-secondary rounded-full flex-shrink-0">
                      <i className={`${t.icon} text-sm`}></i>
                    </span>
                    {t.label}
                  </div>
                ))}
              </div>

              {/* Change Package */}
              <div className="mt-5 pt-5 border-t border-slate-100">
                <Link
                  to="/forfaits"
                  className="text-sky-500 hover:text-sky-600 text-sm font-medium flex items-center gap-1 cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line text-sm"></i></span>
                  Paket ändern
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
}

// Wrapper without Stripe when no key is configured
function PaiementNoStripe() {
  return <PaiementInner />;
}

export default function PaiementWrapper() {
  const { settings } = useAdmin();
  const [stripePromise, setStripePromise] = useState<any>(null);

  useEffect(() => {
    if (settings.paymentProviders?.stripePublicKey) {
      setStripePromise(loadStripe(settings.paymentProviders.stripePublicKey));
    }
  }, [settings.paymentProviders?.stripePublicKey]);

  // Always wrap in Elements — pass null if no key (Stripe will be disabled)
  const fallbackStripe = stripePromise || loadStripe("").catch(() => null);

  return (
    <Elements stripe={stripePromise}>
      <PaiementInner />
    </Elements>
  );
}
